import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { setCurrentStep, setDataV2, setWizardType } from "../onboardingSlice";
import { WIZARD_AI_LOADED, WIZARD_FEED_BUILDER_CLICKED, WIZARD_GENERATE_FEED_CLICKED } from "../../../constants/analyticEvents";
import { pushEvent } from "../../../utilities/analytics";
import { t } from "../../../utilities/translator";
import { LOCALE } from "../../../constants";

const MAX_PROMPT_LENGTH = 256;

const useAIAssistant = () => {
  const dispatch = useAppDispatch();
  const prompt = useAppSelector(state => state.onboarding.formV2.prompt);
  const promptType = useAppSelector(state => state.onboarding.formV2.promptType);

  const [isRecording, setIsRecording] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [error, setError] = useState(null);
  const timestampRef = useRef<number | null>(null);

  const handleStartConversationClick = () => {
    pushEvent(WIZARD_GENERATE_FEED_CLICKED, {
      prompt,
      type: promptType,
    });
  }

  const handleFeedBuilderClick = () => {
    dispatch(setWizardType('wizard'));
    pushEvent(WIZARD_FEED_BUILDER_CLICKED);
  }

  const handleSetPrompt = (prompt: string) => {
    dispatch(setDataV2({ prompt, promptType: 'text' }));
    setError(null);
  }

  const handleSendAudio = async (blob: Blob) => {
    try {
      if (!blob) {
        console.error('No audio recorded');
        return;
      }
      const audioFile = new File([blob], 'recording.wav', { type: 'audio/wav' });

      const formData = new FormData();
      formData.append('file', audioFile, 'recording.wav');
      formData.append('locale', LOCALE);
      setIsAudioLoading(true);
      timestampRef.current = Date.now();

      const response = await fetch(window.apiV6Config.path+'/chat/whisper?type=SEARCH_URL_FUNCTION', {
        method: 'POST',
        body: formData,
      });

      const res = await response.json();

      if (response.ok) {
        dispatch(setDataV2({
          prompt: res.message,
          promptType: 'audio',
          whisper: {
            whisperResponse: res.message,
            whisperStatus: 'Success',
            whisperResponseTime: +((Date.now() - timestampRef.current) / 1000).toFixed(2),
          }
        }));
      } else {
        console.error('Error uploading audio', response.statusText);
        setError(t('onboarding.assistant.errorWhisperNotAvailable'));
        dispatch(setDataV2({
          whisper: {
            whisperResponse: res.message,
            whisperStatus: 'Failed',
            whisperResponseTime: +((Date.now() - timestampRef.current) / 1000).toFixed(2),
          }
        }));
      }
      setIsAudioLoading(false);
    } catch (error) {
      console.error('Error uploading audio', error);
      setError(t('onboarding.assistant.errorWhisperNotAvailable'));
      setIsAudioLoading(false);
    }
  }

  useEffect(() => {
    const wasAIAssistantShown = localStorage.getItem('wasAIAssistantShown');
    if (!wasAIAssistantShown) {
      localStorage.setItem('wasAIAssistantShown', 'true');
    }

    pushEvent(WIZARD_AI_LOADED)
  }, [])

  return {
    prompt,
    isRecording,
    setIsRecording,
    isAudioLoading,
    error,
    setError,
    handleStartConversationClick,
    handleFeedBuilderClick,
    handleSetPrompt,
    handleSendAudio,
    MAX_PROMPT_LENGTH,
  }
}

export default useAIAssistant;
